// =====================================================================
// src/modulos/dashboard.js — o Dashboard do dono (MdM `renderDashboard`, 2386)
//
// É o Início da IMOBILIÁRIA (`visaoDe` de reservas = 'dono'; o login de
// proprietário, que também abria aqui, saiu na 0101). O hotel continua com
// o Painel.
//
// 0101 (Hugo, 02/11/2026, "dashboard e aba"): o botão "Proprietários" no
// topo — o mesmo de `renderFlats` (MdM 10485), que abre o mesmo modal da
// aba Flats (`modulos/proprietarios.js`) — e o seletor "Proprietário" no
// detalhe do quarto, ao lado do "Bloqueio manual" (esboço da 0101, item 4).
// No MdM o dono se troca no formulário do flat; aqui o flat é do hotel e a
// imobiliária só diz de quem é.
//
// Marcação copiada de `renderDashboard` (2386), `_dashRenderKpis` (2340),
// `_dashKpiCard` (2250), `dashboardSection` (1997) e `statCard` (1937):
// a fila de KPIs, a barra de período (De/Até/Aplicar · Hoje · Mês Atual ·
// Mês Anterior · Ano), e uma seção por proprietário com a faixa "Receita
// do dia", os stat-cards e os cards de quarto com as bolinhas que abrem o
// detalhe ao clicar. A conta está em `lib/dashboard.js`, que também diz o
// que ficou de fora e por quê.
//
// O dinheiro só aparece com o módulo `financeiro` no contexto — é o
// "travado por contrato" (Hugo, 20/09). Sem ele, a tela é a de hospedagem.
// =====================================================================
import { hojeDe } from '../lib/contexto.js';
import { el, icone, aviso, enquantoAtualiza } from '../ui.js';
import { moeda, dataBR, corDaPlataforma } from '../lib/reservas.js';
import * as D from '../lib/dashboard.js';
import { abrirIntercorrencias } from './intercorrencias.js';
import { separarIntercorrencias } from '../lib/manutencao.js';
import { OPCOES_BLOQUEIO, possoBloquear, valorParaGravar, avisoDoBloqueio, rotuloDoBloqueio } from '../lib/bloqueio.js';
import { podeVerProprietarios, podeEditarProprietarios, opcoesDoSeletor, valorDoSeletor, avisoDaTroca } from '../lib/proprietarios.js';
import { abrirProprietarios } from './proprietarios.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const nivelDe = ch => ((contexto.modulos || []).find(m => m.chave === ch) || {}).nivel;
  const comFinanceiro = ['ver', 'editar'].includes(nivelDe('financeiro'));
  const temHospedes = ['ver', 'editar'].includes(nivelDe('hospedes'));
  const hoje = () => hojeDe(contexto, agora());
  let vivo = true, unidades = [], reservas = [], valores = new Map(), pendencias = [], chamados = [], proprietarios = [];
  // 0099: o card "Intercorrências" é da IMOBILIÁRIA
  const daImobiliaria = !!contexto.imobiliaria;
  // 0101: o cadastro dos donos é dela, com o módulo `flats`
  const veDonos = podeVerProprietarios(contexto), trocaDono = podeEditarProprietarios(contexto);
  let periodo = D.periodoPadrao(hoje());
  let erro = null;
  const valorDe = r => (valores.has(r.id) ? valores.get(r.id) : null);

  async function carregar() {
    // Tudo que ainda importa: do começo do período (ou do mês, para a
    // receita "até hoje") em diante. Quem corta ao dono é a RLS (0003).
    const desde = periodo.ini < D.mesDe(hoje()).ini ? periodo.ini : D.mesDe(hoje()).ini;
    const emb = 'unidades(numero,andar),imobiliarias(nome)' + (temHospedes ? ',hospedes(nome,principal)' : '');
    const [u, r, p, ch, donos] = await Promise.all([
      auth.ler('unidades', `select=id,numero,andar,imobiliaria_id,proprietario_id,ativo,bloqueio,imobiliarias(nome),proprietarios(nome)&cliente_id=eq.${contexto.cliente.id}&ativo=eq.true&order=numero`),
      auth.ler('reservas', `select=id,unidade_id,imobiliaria_id,numero,plataforma,check_in,check_out,checkout_dia,situacao,observacao,${emb}&cliente_id=eq.${contexto.cliente.id}&check_out=gte.${desde}&order=check_in`),
      // 0067: as reservas que ficaram em quarto bloqueado — as do quarto dela
      // e as que ela trouxe. Sem hóspede, sem valor.
      auth.rpc('reservas_em_quarto_bloqueado', {}),
      // 0099: só os escalados dos flats dela — o corte é do banco
      daImobiliaria ? auth.rpc('chamados_do_cliente', { p_situacao: null }) : Promise.resolve([]),
      // 0101: as opções do seletor "Proprietário" (só quem troca o dono)
      trocaDono ? auth.rpc('proprietarios_da_imobiliaria', {}) : Promise.resolve([]),
    ]);
    unidades = u; reservas = r; valores = new Map(); chamados = ch || []; proprietarios = donos || [];
    pendencias = p || [];
    if (comFinanceiro && reservas.length) {
      try { const v = await auth.rpc('valores_das_reservas', { p_ids: reservas.map(x => x.id) }); for (const x of v) valores.set(x.id, x.valor_total); }
      catch (e) { aviso(e.message, 'erro'); }
    }
  }
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }
  function mudarPeriodo(tipo) {
    const p = D.periodoDe(tipo, hoje());
    const erros = D.validarPeriodo(p);
    if (erros.length) { erro = erros.join(' '); desenhar(); return; }
    erro = null; periodo = p;
    recarregar();
  }

  const ic = (nome, w = 16) => { const i = icone(nome); Object.assign(i.style, { width: w + 'px', height: w + 'px', verticalAlign: '-3px' }); return i; };
  // `_dashKpiCard` (2250)
  function kpiCard({ label, valor, cor, icone: icn, sub, id, onclick, cartao }) {
    const v = String(valor);
    return el('div', { class: 'card', id: cartao, onclick, style: { padding: '14px 16px', borderLeft: '3px solid ' + cor, display: 'flex', flexDirection: 'column', height: '100%', minWidth: '0', overflow: 'hidden', cursor: onclick ? 'pointer' : '' } },
      el('div', { style: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', minHeight: '36px' } },
        el('div', { style: { fontSize: '10.5px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: '1.3', flex: '1', minWidth: '0' } }, label),
        el('div', { style: { color: cor, flexShrink: '0' } }, ic(icn, 18))),
      el('div', { id, style: { fontSize: v.length > 5 ? '14px' : '16px', fontWeight: '800', color: cor, marginTop: 'auto', lineHeight: '1', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, v),
      el('div', { style: { fontSize: '10.5px', color: '#9ca3af', marginTop: '3px', minHeight: '14px' } }, sub || ' '));
  }
  // `statCard` (1937)
  const statCard = (label, val, cor, small, classe, id) => el('div', { class: 'stat-card ' + (classe || ''), id },
    el('div', { style: { fontSize: small ? '16px' : '20px', fontWeight: '700', color: cor } }, String(val)),
    el('div', { class: 'stat-label' }, label));

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'dashboard';
    const h = hoje();
    const mesAtual = D.eMesAtual(periodo, h);
    const raiz = el('div', { class: 'fade-in' + (mesAtual ? '' : ' dash-modo-periodo'), id: 'dash' });
    // o `<h1>Dashboard</h1>` do MdM já é o título do cabeçalho do app (main.js)
    // 0101: o botão "Proprietários" do topo de `renderFlats` (10485), na mesma
    // linha flex de lá; o título à esquerda é o do cabeçalho do app
    if (veDonos) raiz.appendChild(el('div', { style: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' } },
      el('div', { style: { display: 'flex', gap: '8px' } },
        el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-proprietarios',
          onclick: () => abrirProprietarios({ auth, contexto, aoMudar: recarregar }) }, ic('ic-flats', 16), ' Proprietários'))));
    if (pendencias.length) raiz.appendChild(bannerPendencias());

    // os KPIs do topo (2340) — os de hospedagem; com financeiro, os de receita
    const k = D.kpis(reservas, h, { comFinanceiro, valorDe });
    raiz.appendChild(el('div', { class: 'dash-today', id: 'dash-kpis', style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '10px', marginBottom: '18px' } },
      kpiCard({ label: 'Hospedados hoje', valor: k.hospedadosHoje, cor: '#1B3A7A', icone: 'ic-hotel', id: 'kpi-hospedados' }),
      kpiCard({ label: 'Check-ins amanhã', valor: k.checkinsAmanha, cor: '#0f766e', icone: 'ic-checkin', id: 'kpi-checkins' }),
      kpiCard({ label: 'Check-outs amanhã', valor: k.checkoutsAmanha, cor: '#d97706', icone: 'ic-door', id: 'kpi-checkouts' }),
      // 0099: "Intercorrências" do MdM (_dashRenderKpis, 2340), na mesma posição
      daImobiliaria ? kpiCard({ label: 'Intercorrências', valor: separarIntercorrencias(chamados).pendentes.length, cor: '#dc2626', icone: 'ic-warning', sub: 'pendentes', id: 'kpi-intercorrencias', cartao: 'card-intercorrencias',
        onclick: () => abrirIntercorrencias({ auth, linhas: chamados, aoMudar: recarregar }) }) : null,
      comFinanceiro ? kpiCard({ label: 'Receita do dia', valor: moeda(k.receitaHoje), cor: '#0d9488', icone: 'ic-financeiro', sub: 'pro-rata por noites', id: 'kpi-receita-dia' }) : null,
      comFinanceiro ? kpiCard({ label: 'Receita no mês', valor: moeda(k.receitaMes), cor: '#059669', icone: 'ic-financeiro', sub: 'até hoje', id: 'kpi-receita-mes' }) : null));

    // a barra de período (2416)
    const btnSec = (t, tipo) => el('button', { type: 'button', class: 'btn btn-secondary', dataset: { periodo: tipo }, style: { padding: '6px 10px', fontSize: '12px' }, onclick: () => mudarPeriodo(tipo) }, t);
    const form = el('form', { class: 'card', id: 'dash-periodo', style: { padding: '14px 16px', marginBottom: '20px' }, onsubmit: ev => { ev.preventDefault(); mudarPeriodo({ ini: form.querySelector('#dash-ini').value, fim: form.querySelector('#dash-fim').value }); } },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } },
        el('span', { style: { fontSize: '13px', fontWeight: '600', color: '#374151' } }, 'Período:'),
        el('label', { for: 'dash-ini', style: { fontSize: '12px', color: '#6b7280' } }, 'De'),
        el('input', { type: 'date', id: 'dash-ini', value: periodo.ini, style: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' } }),
        el('label', { for: 'dash-fim', style: { fontSize: '12px', color: '#6b7280' } }, 'Até'),
        el('input', { type: 'date', id: 'dash-fim', value: periodo.fim, style: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' } }),
        el('button', { type: 'submit', class: 'btn btn-primary', id: 'dash-aplicar', style: { padding: '6px 14px', fontSize: '13px' } }, 'Aplicar'),
        el('div', { style: { display: 'flex', gap: '6px', marginLeft: '4px', flexWrap: 'wrap' } }, btnSec('Hoje', 'hoje'), btnSec('Mês Atual', 'mesAtual'), btnSec('Mês Anterior', 'mesAnterior'), btnSec('Ano', 'ano')),
        el('span', { id: 'dash-rotulo', style: { marginLeft: 'auto', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' } }, D.rotuloDoPeriodo(periodo, h))),
      erro ? el('div', { class: 'alert alert-error', id: 'dash-erro', role: 'alert', style: { marginTop: '10px' } }, erro) : null);
    raiz.appendChild(form);

    // uma seção por proprietário (2396)
    const grupos = D.gruposDoDono(unidades);
    grupos.forEach((g, i) => {
      raiz.appendChild(secaoDoDono(D.secao(g.titulo, g.quartos, reservas, h, periodo, { comFinanceiro, valorDe })));
      if (i < grupos.length - 1) raiz.appendChild(el('div', { style: { borderTop: '2px dashed #e5e7eb', marginBottom: '28px' } }));
    });
    if (!grupos.length) raiz.appendChild(el('p', { class: 'vazio' }, 'Nenhum quarto cadastrado para você.'));
    cont.appendChild(raiz);
  }

  // 0067: as reservas que ficaram em quarto bloqueado. Não existe no MdM
  // (lá o bloqueio não tem dono nem pendência); o banner é o do overbooking
  // da Lista (`bannerOverbooking`, MdM 2560) em âmbar — a cor do
  // "Indisponível". Quem resolve: move, encurta ou cancela a reserva, ou
  // desbloqueia o quarto.
  function bannerPendencias() {
    const b = t => el('strong', null, String(t));
    return el('div', { id: 'dash-pendencias', dataset: { n: String(pendencias.length) }, style: { background: '#fffbeb', border: '2px solid #d97706', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px', display: 'flex', alignItems: 'flex-start', gap: '12px', boxShadow: '0 0 0 4px rgba(217,119,6,0.15)' } },
      el('span', { style: { fontSize: '28px', color: '#d97706' } }, ic('ic-warning', 26)),
      el('div', { style: { flex: '1', minWidth: '0' } },
        el('div', { style: { fontWeight: '800', color: '#92400e', fontSize: '16px' } }, 'Reservas em quarto bloqueado'),
        el('div', { style: { fontSize: '12px', color: '#92400e', marginTop: '3px' } }, b(pendencias.length), ' reserva(s) confirmada(s) num quarto Indisponível ou em Manutenção. Mude de quarto, encurte ou cancele a reserva — ou desbloqueie o quarto.'),
        el('div', { style: { marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' } },
          pendencias.map(p => el('div', { dataset: { pendencia: p.reserva_id }, style: { fontSize: '12px', color: '#78350f', background: '#fff', border: '1px solid #fde68a', borderRadius: '6px', padding: '6px 10px' } },
            el('strong', null, 'Quarto ' + p.quarto), ` · ${rotuloDoBloqueio(p.bloqueio)} · ${dataBR(p.check_in)} → ${dataBR(p.check_out)}`,
            p.numero_reserva ? ` · reserva ${p.numero_reserva}` : '', p.plataforma ? ` · ${p.plataforma}` : '',
            p.imobiliaria ? ` · ${p.imobiliaria}` : '')))));
  }

  // O "Bloqueio manual" do MdM (10430) no detalhe do card — só para quem
  // responde pelo quarto. O clique no seletor não fecha o detalhe.
  function seletorBloqueio(q) {
    if (!possoBloquear(q, contexto)) return null;
    const sel = el('select', { class: 'input', dataset: { bloqueio: q.id }, style: { fontSize: '12px', padding: '4px 6px', marginTop: '4px' },
      onclick: ev => ev.stopPropagation(),
      onchange: async ev => {
        const v = valorParaGravar(ev.target.value);
        if (v === undefined) return;
        let n;
        try { n = await auth.rpc('definir_bloqueio', { p_unidade: q.id, p_bloqueio: v }); }
        catch (e) { aviso(e.message, 'erro'); ev.target.value = q.bloqueio || ''; return; }
        aviso(avisoDoBloqueio(q.numero, v, n));
        recarregar();
      } },
      OPCOES_BLOQUEIO.map(o => el('option', { value: o.valor, selected: (q.bloqueio || '') === o.valor }, o.rotulo)));
    return el('div', { class: 'form-row', style: { margin: '8px 0 0' }, onclick: ev => ev.stopPropagation() },
      el('label', { class: 'label', style: { fontSize: '11px' } }, 'Bloqueio manual'), sel);
  }

  // 0101: o dono do quarto, ao lado do "Bloqueio manual" (esboço, item 4) —
  // só para a imobiliária com `editar flats`, e só no flat DELA. O mesmo
  // seletor do card da aba Flats (opções em lib/proprietarios.js).
  function seletorProprietario(q) {
    if (!trocaDono || !q.imobiliaria_id || q.imobiliaria_id !== contexto.imobiliaria.id) return null;
    const atual = q.proprietario_id || '';
    const sel = el('select', { class: 'input', dataset: { proprietarioDoFlat: q.id }, style: { fontSize: '12px', padding: '4px 6px', marginTop: '4px' },
      onclick: ev => ev.stopPropagation(),
      onchange: async ev => {
        const novo = valorDoSeletor(ev.target.value);
        try { await auth.rpc('definir_proprietario_do_flat', { p_unidade: q.id, p_proprietario: novo }); }
        catch (e) { aviso(e.message, 'erro'); ev.target.value = atual; return; }
        aviso(avisoDaTroca(q.numero, novo ? (proprietarios.find(p => p.id === novo) || {}).nome : null));
        recarregar();
      } },
      opcoesDoSeletor(proprietarios, q.proprietario_id).map(o => el('option', { value: o.valor, selected: o.valor === atual, disabled: o.desabilitado }, o.rotulo)));
    return el('div', { class: 'form-row', style: { margin: '8px 0 0' }, onclick: ev => ev.stopPropagation() },
      el('label', { class: 'label', style: { fontSize: '11px' } }, 'Proprietário'), sel);
  }

  // `dashboardSection` (1997)
  function secaoDoDono(s) {
    const cor = '#1B3A7A';
    const sec = el('div', { dataset: { secao: s.titulo }, style: { marginBottom: '28px' } },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' } },
        el('div', { style: { width: '4px', height: '24px', background: cor, borderRadius: '2px' } }),
        el('h2', { style: { fontSize: '18px', fontWeight: '700', color: cor, margin: '0' } }, 'Dashboard — ' + s.titulo),
        el('span', { style: { fontSize: '13px', color: '#6b7280', fontWeight: '400' } }, `(${s.quartos.length} quarto${s.quartos.length !== 1 ? 's' : ''})`)));
    if (s.comFinanceiro) {
      sec.appendChild(el('div', { class: 'dash-today', dataset: { receitaHoje: '1' }, style: { background: `linear-gradient(135deg,${cor},#2563eb)`, color: '#fff', borderRadius: '12px', padding: '14px 18px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' } },
        el('div', null,
          el('div', { style: { fontSize: '11px', fontWeight: '600', opacity: '0.85', textTransform: 'uppercase', letterSpacing: '0.08em' } }, 'Receita do dia · ' + s.titulo),
          el('div', { style: { fontSize: '11px', opacity: '0.75', marginTop: '2px' } }, 'Pro-rata por noites')),
        el('div', { style: { fontSize: '24px', fontWeight: '800', letterSpacing: '-0.02em', textAlign: 'right' } }, moeda(s.receitaHoje))));
    }
    sec.appendChild(el('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: '8px', marginBottom: '14px' } },
      statCard('Reservas Ativas', s.ativas, '#1B3A7A', false, 'dash-today'),
      statCard('Check-ins Hoje', s.checkinHoje, '#059669', false, 'dash-today'),
      statCard('Check-outs Hoje', s.checkoutHoje, '#dc2626', false, 'dash-today'),
      // os cards do período (finCards, 2059): sem financeiro, só a contagem —
      // Despesas e Saldo entram com o módulo Financeiro (lançamentos)
      s.comFinanceiro ? statCard('Reservas no Período', s.reservasNoPeriodo, '#1B3A7A', true, 'dash-periodo') : null,
      s.comFinanceiro ? statCard('Receitas', moeda(s.receitaPeriodo), '#7c3aed', true, 'dash-periodo', null) : null));
    const dot = (c, on) => el('span', { style: { width: '14px', height: '14px', borderRadius: '50%', display: 'inline-block', background: on ? c : '#e5e7eb', boxShadow: on ? `0 0 5px ${c}88` : 'none', transition: 'background 0.2s' } });
    const leg = (c, t) => el('span', { style: { display: 'flex', alignItems: 'center', gap: '6px' } }, el('span', { style: { width: '14px', height: '14px', borderRadius: '50%', background: c, display: 'inline-block', boxShadow: `0 0 5px ${c}88` } }), el('strong', null, t));
    sec.appendChild(el('div', { class: 'card' },
      el('div', { style: { display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '16px', fontSize: '13px', color: '#374151', flexWrap: 'wrap' } },
        ...D.BOLINHAS.map(([c, , t]) => leg(c, t)),
        el('span', { style: { fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' } }, 'clique no quarto para ver detalhes')),
      el('h3', { style: { fontSize: '14px', fontWeight: '600', margin: '0 0 12px' } }, 'Próximos Check-ins'),
      el('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '10px' } },
        s.quartos.length ? s.estados.map(e => {
          const borda = D.BORDA[e.estado] || D.BORDA.disponivel;
          const det = el('div', { class: 'hidden', dataset: { detalhe: e.quarto.id }, style: { marginTop: '10px', textAlign: 'left', fontSize: '11px', color: '#374151', borderTop: '1px solid #f3f4f6', paddingTop: '8px', lineHeight: '1.8' } },
            D.linhasDoDetalhe(e, { comFinanceiro: s.comFinanceiro, valorDe, dataBR }).flatMap((l, i) => [i ? el('br') : null,
              ...(l.plataforma ? [l.texto, el('span', { style: { background: corDaPlataforma(l.plataforma), color: '#fff', fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '6px', textTransform: 'uppercase' } }, l.plataforma)]
              : 'valor' in l ? [l.texto + moeda(l.valor)]
              : l.forte ? [el('strong', null, l.texto)]
              : l.cor ? [el('span', { style: { color: l.cor, fontWeight: '600' } }, l.texto)] : [l.texto])]),
            seletorBloqueio(e.quarto), seletorProprietario(e.quarto));
          return el('div', { dataset: { quarto: e.quarto.id, estado: e.estado }, onclick: () => det.classList.toggle('hidden'),
            style: { cursor: 'pointer', border: '2px solid ' + borda, borderRadius: '14px', padding: '18px 14px', textAlign: 'center', background: '#fff', minWidth: '100px', flex: '0 0 auto', transition: 'box-shadow 0.15s' } },
            el('div', { style: { fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '10px' } }, 'Quarto ' + e.quarto.numero),
            el('div', { style: { display: 'flex', justifyContent: 'center', gap: '7px' } }, D.BOLINHAS.map(([c, est]) => dot(c, e.estado === est))),
            det);
        }) : el('p', { style: { color: '#6b7280', fontSize: '13px' } }, 'Nenhum quarto cadastrado.'))));
    return sec;
  }

  recarregar();
  return () => { vivo = false; };
}
